/**
 * Delivery Zone Utilities
 *
 * Functions for determining delivery zones and times based on postal codes
 * and country delivery configurations from Firestore.
 */

/**
 * Sanitize postal code by removing spaces and converting to uppercase
 * @param {string} postalCode - Raw postal code input
 * @returns {string} Sanitized postal code
 */
export const sanitizePostalCode = (postalCode) => {
  if (!postalCode) return "";
  return String(postalCode).replace(/\s+/g, "").toUpperCase();
};

/**
 * Find delivery zone information for a given postal code
 *
 * @param {string} postalCode - The postal code to check
 * @param {object} countryDeliveryConfig - Country delivery configuration from Firestore
 * @returns {object|null} Delivery zone info or null if not found
 *
 * Returns object with:
 * - deliveryLabel: string (e.g., "FB24D")
 * - deliveryTime: string (e.g., "24H")
 * - deliveryDays: array of numbers (e.g., [1, 3, 5])
 * - color: string (for Tag display)
 */
export const findDeliveryZoneByPostalCode = (postalCode, countryDeliveryConfig) => {
  if (!postalCode || !countryDeliveryConfig?.deliveryLabels) {
    return null;
  }

  const sanitized = sanitizePostalCode(postalCode);

  // Find label with matching postal code in deliveryZones array
  const matchedLabel = countryDeliveryConfig.deliveryLabels.find((label) => {
    if (!label.deliveryZones || label.deliveryZones.length === 0) {
      return false; // Skip fallback labels
    }
    return label.deliveryZones.includes(sanitized);
  });

  if (matchedLabel) {
    return {
      deliveryLabel: matchedLabel.deliveryLabel,
      deliveryTime: `${matchedLabel.leadDays * 24}H`,
      deliveryDays: matchedLabel.deliveryDays,
      color: getDeliveryZoneColor(matchedLabel.leadDays),
    };
  }

  // If no match found, return the fallback label (one without deliveryZones)
  const fallbackLabel = countryDeliveryConfig.deliveryLabels.find((label) => {
    return !label.deliveryZones || label.deliveryZones.length === 0;
  });

  if (fallbackLabel) {
    return {
      deliveryLabel: fallbackLabel.deliveryLabel,
      deliveryTime: `${fallbackLabel.leadDays * 24}H`,
      deliveryDays: fallbackLabel.deliveryDays,
      color: getDeliveryZoneColor(fallbackLabel.leadDays),
    };
  }

  return null;
};

/**
 * Get color for delivery zone Tag based on lead days
 * @param {number} leadDays - Number of lead days
 * @returns {string} Ant Design color name
 */
const getDeliveryZoneColor = (leadDays) => {
  if (leadDays === 1) return "green"; // 24H
  if (leadDays === 2) return "blue"; // 48H
  if (leadDays === 3) return "orange"; // 72H
  return "default";
};

/**
 * Get delivery zone status (for backward compatibility)
 * @param {object} zone - Zone object with deliveryTime
 * @returns {object} Object with color property
 */
export const getDeliveryZoneStatus = (zone) => {
  if (!zone || !zone.deliveryTime) {
    return { color: "default" };
  }

  const hours = parseInt(zone.deliveryTime);
  const leadDays = Math.ceil(hours / 24);

  return {
    color: getDeliveryZoneColor(leadDays),
  };
};

/**
 * Reusable component to render delivery zone badge
 * @param {Object} props - Component props
 * @param {string} props.zipCode - Postal code to check
 * @param {string} props.country - Country code (e.g., "FR")
 * @param {object} props.countryConfig - Country delivery configuration from Firestore
 * @param {boolean} props.showForAllCountries - Show badge for all countries (default: false, only FR)
 * @param {boolean} props.showTooltip - Show tooltip with zone details (default: true)
 * @returns {JSX.Element|null} - Badge component or null
 */
export const DeliveryZoneBadge = ({
  zipCode,
  country,
  countryConfig,
  showForAllCountries = false,
  showTooltip = true,
}) => {
  const React = require("react");
  const { Badge, Tooltip } = require("antd");

  // Only show for French customers unless explicitly requested for all countries
  if (!showForAllCountries && country !== "FR") {
    return null;
  }

  if (!zipCode || !countryConfig) {
    const badge = <Badge status="default" />;
    return badge;
  }

  const sanitized = sanitizePostalCode(zipCode);
  const deliveryZone = findDeliveryZoneByPostalCode(sanitized, countryConfig);

  if (!deliveryZone) {
    const badge = <Badge status="warning" />;
    return showTooltip ? (
      <Tooltip title="Postal code not in delivery list - requires approval">{badge}</Tooltip>
    ) : (
      badge
    );
  }

  // Use color prop for static badges (status="processing" animates)
  const badge = <Badge color={deliveryZone.color} />;

  if (!showTooltip) {
    return badge;
  }

  const tooltipTitle = `${deliveryZone.deliveryTime} delivery`;

  return <Tooltip title={tooltipTitle}>{badge}</Tooltip>;
};
